import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { UserPlus, Wallet, CreditCard } from 'lucide-react';

const steps = [
  {
    number: '01',
    icon: UserPlus,
    title: 'Create your wallet',
    description: 'Sign up in minutes with a streamlined verification process. No complex paperwork, no delays.',
  },
  {
    number: '02',
    icon: Wallet,
    title: 'Fund your account',
    description: 'Top up your balance using bank transfers, cards, or local payment methods in your preferred currency.',
  },
  {
    number: '03',
    icon: CreditCard,
    title: 'Pay or transfer globally',
    description: 'Scan QR codes, send SWIFT/SEPA transfers, or pay international services — all from one wallet.',
  },
];

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

export const HowItWorks = () => {
  return (
    <section id="how-it-works" className="py-16 sm:py-20 bg-white" data-testid="how-it-works-section">
      <div className="container-main">
        <div className="text-center mb-12">
          <p className="font-mono-accent text-xs font-medium uppercase tracking-[0.18em] text-[#7C3AED] mb-3">
            How it works
          </p>
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#0B0B10] mb-4">
            Three simple steps to global payments
          </h2>
          <p className="text-base text-[#64647A] max-w-lg mx-auto">
            Getting started with TexSolution is fast and straightforward. Here's how.
          </p>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {steps.map((step, index) => (
            <motion.div key={index} variants={cardVariants}>
              <Card
                data-testid={`how-it-works-step-${index + 1}`}
                className="bg-white border border-[#E7E7EF] rounded-2xl shadow-sm card-hover h-full"
              >
                <CardContent className="p-6 sm:p-7">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="font-mono-accent text-xs font-medium text-[#A78BFA]">{step.number}</span>
                    <div className="h-10 w-10 rounded-xl bg-[#F5F3FF] text-[#7C3AED] flex items-center justify-center">
                      <step.icon className="h-5 w-5" />
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-[#0B0B10] mb-2">{step.title}</h3>
                  <p className="text-sm text-[#64647A] leading-relaxed">{step.description}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
